import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import BrainIcon from '../assets/brain.svg';
import ThunderIcon from '../assets/thunder.svg';
import HandshakeIcon from '../assets/handshake.svg';
import CallIcon from '../assets/call.svg';


const WhyUs = () => {
  const features = [
    {
      title: 'Book More Sales Calls with AI, No More Missed Leads.',
      description: 'AI-powered voice agents automatically follow up with leads, qualify prospects, and schedule calls—ensuring no opportunity slips through the cracks.'
    },
    {
      title: 'Engage Customers Instantly, AI Responds in Seconds, Not Hours.',
      description: 'Forget long wait times—our AI voice agents handle inbound and outbound calls instantly, providing real-time responses that drive conversions.'
    },
    {
      title: 'Grow Faster, Let AI Take Over Your Calls.',
      description: 'Sonor AI automates calls so you can focus on scaling your business without added costs.'
    }
  ];

  return (
    <Section id="why-us">
      <SectionTop>
        <WhyUsTag>Why Sonor AI</WhyUsTag>
      </SectionTop>

      <MainHeading>
        AI-Powered <HighlightText>Voice Agents</HighlightText> That Convert
      </MainHeading>

      <Description>
        Engage, Convert, and Scale Effortlessly
      </Description>

      <FeatureCardsContainer>
        {features.map((feature, index) => (
          <FeatureCard
            key={index}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: index * 0.2 }}
          >
            <CardContent>
              <IconContainer>
                <IconWrapper>
                  {index === 0 && <img src={CallIcon} alt="Call Icon" width="75" height="75" />}
                  {index === 1 && <img src={ThunderIcon} alt="Thunder Icon" width="75" height="75" />}
                  {index === 2 && <img src={HandshakeIcon} alt="Handshake Icon" width="75" height="75" />}
                </IconWrapper>
              </IconContainer>

              <CardTextContent>
                <FeatureTitle>{feature.title}</FeatureTitle>
                <FeatureDescription>{feature.description}</FeatureDescription>
              </CardTextContent>
            </CardContent>
          </FeatureCard>
        ))}
      </FeatureCardsContainer>
      <BookCallButton href="https://cal.com/sonorai/intro" target="_blank" rel="noopener noreferrer">
        Book A Call <ArrowIcon>→</ArrowIcon>
      </BookCallButton>
    </Section>
  );
};

const Section = styled.section`
  padding: 100px 20px;
  max-width: 1200px;
  margin: 0 auto;
  text-align: center;
`;

const SectionTop = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 30px;
`;

const WhyUsTag = styled.div`
  background-color: rgba(17, 17, 17, 0.9);
  color: white;
  font-size: 14px;
  font-weight: 500;
  padding: 8px 24px;
  border-radius: 50px;
  display: inline-block;
`;

const MainHeading = styled.h2`
  font-size: 38px;
  font-weight: 700;
  color: white;
  margin-bottom: 15px;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 32px;
  }

  @media (max-width: 480px) {
    font-size: 26px;
  }
`;

const HighlightText = styled.span`
  color: var(--color-text-light);
`;

const Description = styled.p`
  font-size: 18px;
  color: var(--color-text-gray);
  margin-bottom: 60px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const FeatureCardsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 25px;
  margin-bottom: 50px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled(motion.div)`
  background-color: rgba(21, 21, 21, 0.8);
  border-radius: 20px;
  overflow: hidden;
  height: 100%;
`;

const CardContent = styled.div`
  padding: 25px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  height: 100%;
`;

const IconContainer = styled.div`
  width: 100%;
  height: 160px;
  margin-bottom: 25px;
  background-color: #0A0A0A;
  border-radius: 10px;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image:
      radial-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px);
    background-size: 10px 10px;
    opacity: 0.5;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image:
      radial-gradient(white 0.5px, transparent 0.5px);
    background-size: 30px 30px;
    opacity: 0.2;
  }
`;

const IconWrapper = styled.div`
  position: relative;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CardTextContent = styled.div`
  text-align: left;
  width: 100%;
`;

const FeatureTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: white;
  margin-bottom: 12px;
`;

const FeatureDescription = styled.p`
  font-size: 15px;
  line-height: 1.5;
  color: var(--color-text-gray);
`;

const BookCallButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background-color: transparent;
  border: none;
  color: var(--color-ui-orange);
  font-size: 18px;
  font-weight: 500;
  cursor: pointer;
  padding: 10px 0;
  margin-bottom: 60px;
  transition: all 0.3s ease;

  &:hover {
    gap: 12px;
  }
`;

const ArrowIcon = styled.span`
  font-size: 20px;
  transition: transform 0.3s ease;

  ${BookCallButton}:hover & {
    transform: translateX(3px);
  }
`;

export default WhyUs;
